import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme, Switch, Divider, InputLabel, MenuItem, Select, FormControl, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import { useCreateCollection } from "../../hooks/collections/useCreateCollection";
import { useUpdateCollection } from "../../hooks/collections/useUpdateCollection";
import { useSyncCollectionProducts } from "../../hooks/collections/useSyncCollectionProducts";

import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";
import { useGetProducts } from "../../hooks/products/useGetProducts";
import { useGetCategories } from "../../hooks/categories/useGetCategories";
import { useGetBrands } from "../../hooks/brands/useGetBrands";
import { useGetTags } from "../../hooks/tags/useGetTags";

const FormAddEditCollection = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Form States
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [slug, setSlug] = useState("");
    const [type, setType] = useState("manual");
    const [rules, setRules] = useState([]);
    const [isActive, setIsActive] = useState(true);
    const [selectedProductIds, setSelectedProductIds] = useState([]);

    // Hooks
    const { createCollectionMutate, isPending: isCreating } = useCreateCollection();
    const { editCollection, isUpdating } = useUpdateCollection();
    const { syncProducts, isSyncing } = useSyncCollectionProducts();
    const { products, isLoading: loadingProducts } = useGetProducts({ per_page: 1000 });
    const { categories, isLoading: loadingCategories } = useGetCategories({ per_page: 1000 });
    const { brands, isLoading: loadingBrands } = useGetBrands({ per_page: 1000 });
    const { tags, isLoading: loadingTags } = useGetTags({ per_page: 1000 });

    const loadingSubmit = isCreating || isUpdating || isSyncing;

    // Auto-slug generation from English title
    useEffect(() => {
        if (titleEn && !isEdit) {
            setSlug(slugify(titleEn));
        }
    }, [titleEn, isEdit]);

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData?.title?.ar || "");
            setTitleEn(currentData?.title?.en || "");
            setSlug(currentData.slug || "");
            setType(currentData.type || "manual");

            // Handle rules - convert to array if needed
            if (currentData.rule) {
                if (Array.isArray(currentData.rule)) {
                    setRules(currentData.rule);
                } else {
                    setRules([]);
                }
            } else {
                setRules([]);
            }

            setIsActive(currentData.is_active ?? true);

            // Handle product_ids from API
            if (currentData.product_ids && Array.isArray(currentData.product_ids)) {
                setSelectedProductIds(currentData.product_ids);
            } else if (currentData.products && Array.isArray(currentData.products)) {
                setSelectedProductIds(currentData.products.map(p => p.id));
            }
        }
    }, [isEdit, currentData]);

    const handleProductChange = (e) => {
        setSelectedProductIds(e.target.value);
    };

    const addRule = () => {
        setRules(prev => [...prev, { type: "on_sale", value: true, operator: "and" }]);
    };

    const removeRule = (index) => {
        setRules(prev => prev.filter((_, i) => i !== index));
    };

    const updateRule = (index, field, value) => {
        setRules(prev => prev.map((rule, i) => {
            if (i === index) {
                const newRule = { ...rule, [field]: value };

                // Reset value when type changes
                if (field === 'type') {
                    switch (value) {
                        case 'category':
                        case 'brand':
                        case 'tag':
                            newRule.value = [];
                            break;
                        case 'price_range':
                            newRule.value = { min: 0, max: 100 };
                            break;
                        case 'new_arrivals':
                            newRule.value = 30;
                            break;
                        case 'on_sale':
                            newRule.value = true;
                            break;
                        default:
                            newRule.value = "";
                    }
                }

                return newRule;
            }
            return rule;
        }));
    };

    const SubmitForm = (e) => {
        e.preventDefault();

        const payload = {
            title: {
                en: titleEn || titleAr,
                ar: titleAr || titleEn
            },
            slug,
            type,
            is_active: isActive
        };

        // Add rules for automatic collections
        if (type === 'automatic' && rules.length > 0) {
            payload.rule = rules.map(r => ({
                type: r.type,
                value: r.value,
                operator: r.operator || "and"
            }));
        } else if (type === 'manual') {
            payload.rule = null;
        }

        if (isEdit && currentData?.id) {
            editCollection(
                { id: currentData.id, data: payload },
                {
                    onSuccess: () => {
                        // Sync only happens in manual mode
                        if (type === 'manual') {
                            syncProducts(
                                { id: currentData.id, product_ids: selectedProductIds },
                                { onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); } }
                            );
                        } else {
                            if (onSubmitSuccess) onSubmitSuccess();
                        }
                    }
                }
            );
        } else {
            createCollectionMutate(payload, {
                onSuccess: (response) => {
                    if (onSubmitSuccess) onSubmitSuccess(response);
                }
            });
        }
    };

    const renderRuleValueInput = (rule, index) => {
        switch (rule.type) {
            case 'category':
                return (
                    <FormInput
                        label={isArabic ? "الفئات" : "Categories"}
                        type="multi_select"
                        selectedValue={rule.value || []}
                        handleSelectChange={(e) => updateRule(index, 'value', e.target.value)}
                        options={(categories || []).map(c => ({
                            id: c.id,
                            name: {
                                ar: c.name?.ar || c.name?.en,
                                en: c.name?.en || c.name?.ar
                            }
                        }))}
                        disabled={loadingCategories}
                    />
                );

            case 'brand':
                return (
                    <FormInput
                        label={isArabic ? "العلامات التجارية" : "Brands"}
                        type="multi_select"
                        selectedValue={rule.value || []}
                        handleSelectChange={(e) => updateRule(index, 'value', e.target.value)}
                        options={(brands || []).map(b => ({
                            id: b.id,
                            name: {
                                ar: b.name?.ar || b.name?.en,
                                en: b.name?.en || b.name?.ar
                            }
                        }))}
                        disabled={loadingBrands}
                    />
                );

            case 'tag':
                return (
                    <FormInput
                        label={isArabic ? "الوسوم" : "Tags"}
                        type="multi_select"
                        selectedValue={rule.value || []}
                        handleSelectChange={(e) => updateRule(index, 'value', e.target.value)}
                        options={(tags || []).map(t => ({
                            id: t.id,
                            name: {
                                ar: t.name?.ar || t.name?.en,
                                en: t.name?.en || t.name?.ar
                            }
                        }))}
                        disabled={loadingTags}
                    />
                );

            case 'price_range':
                return (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label={isArabic ? "الحد الأدنى" : "Min Price"}
                            type="number"
                            value={rule.value?.min || 0}
                            onChange={(e) => updateRule(index, 'value', { ...rule.value, min: parseFloat(e.target.value) })}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label={isArabic ? "الحد الأقصى" : "Max Price"}
                            type="number"
                            value={rule.value?.max || 100}
                            onChange={(e) => updateRule(index, 'value', { ...rule.value, max: parseFloat(e.target.value) })}
                            fullWidth
                            size="small"
                        />
                    </Box>
                );

            case 'new_arrivals':
                return (
                    <TextField
                        label={isArabic ? "عدد الأيام" : "Days"}
                        type="number"
                        value={rule.value || 30}
                        onChange={(e) => updateRule(index, 'value', parseInt(e.target.value))}
                        fullWidth
                        size="small"
                        helperText={isArabic ? "المنتجات المضافة في آخر X يوم" : "Products added in last X days"}
                    />
                );

            case 'on_sale':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2">
                            {isArabic ? "المنتجات التي بها خصم" : "Products with discount"}
                        </Typography>
                        <Switch
                            checked={!!rule.value}
                            onChange={(e) => updateRule(index, 'value', e.target.checked)}
                            color="secondary"
                        />
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 4 },
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            {isEdit ? (isArabic ? "تعديل المجموعه" : "Edit Collection") : (isArabic ? "إضافة مجموعه جديدة" : "Create New Collection")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isArabic ? "قم بتعبئة البيانات الأساسية للمجموعة واختيار نوعها" : "Fill in the basic info and select the collection type"}
                        </Typography>
                    </Box>

                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={4}>
                    {/* Basic Info Section */}
                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "البيانات الأساسية" : "Basic Information"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {activeLang === "ar" ? (
                            <FormInput
                                label="العنوان (عربي)"
                                placeholder="مثلاً: مجموع الشتاء"
                                type="text"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                            />
                        ) : (
                            <FormInput
                                label="Title (English)"
                                placeholder="e.g., Winter Collection"
                                type="text"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                            />
                        )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug)" : "Slug"}
                            placeholder="winter-collection"
                            type="text"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            required
                            disabled={isEdit}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "نوع المجموعه" : "Type"}
                            type="select"
                            name="type"
                            value={type}
                            onChange={(e) => {
                                setType(e.target.value);
                                // Reset rules when changing type
                                if (e.target.value === 'manual') {
                                    setRules([]);
                                }
                            }}
                            options={[
                                { id: "manual", name: { ar: "يدوي", en: "Manual" } },
                                { id: "automatic", name: { ar: "تلقائي", en: "Automatic" } },
                            ]}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <InputLabel
                            sx={{
                                my: 1,
                                fontWeight: 'bold',
                                textAlign: isArabic ? 'right' : 'left',
                                direction: isArabic ? 'rtl' : 'ltr',
                            }}
                        >
                            {isArabic ? "حالة المجموعة" : "Status"}
                        </InputLabel>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2.5,
                            py: 1.5,
                            border: '1px solid',
                            borderColor: theme => theme.palette.divider,
                            borderRadius: '12px',
                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
                            transition: 'all 0.3s ease',
                        }}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {isActive ? (isArabic ? "نشط" : "Active") : (isArabic ? "غير نشط" : "Inactive")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {isActive ? (isArabic ? "هذه المجموعة ستظهر للعملاء" : "Visible to customers") : (isArabic ? "هذه المجموعة مخفية حالياً" : "Hidden from customers")}
                                </Typography>
                            </Box>
                            <Switch
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                color="primary"
                            />
                        </Box>
                    </Grid>

                    {/* Automatic Collection Rules */}
                    {type === "automatic" && (
                        <Grid item xs={12}>
                            <Typography variant="overline" color="text.secondary" fontWeight="bold">
                                {isArabic ? "قواعد المجموعة" : "Collection Rules"}
                            </Typography>
                            <Divider sx={{ mb: 3, mt: 1 }} />

                            {rules.length === 0 && (
                                <Box sx={{ p: 3, bgcolor: 'info.light', color: 'info.contrastText', borderRadius: 2, mb: 2 }}>
                                    <Typography variant="body2">
                                        {isArabic ? "⚠️ يجب إضافة قاعدة واحدة على الأقل للمجموعة التلقائية" : "⚠️ At least one rule is required for automatic collections"}
                                    </Typography>
                                </Box>
                            )}

                            {rules.map((rule, index) => (
                                <Paper key={index} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {isArabic ? `قاعدة #${index + 1}` : `Rule #${index + 1}`}
                                        </Typography>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => removeRule(index)}
                                        >
                                            {isArabic ? "حذف" : "Remove"}
                                        </Button>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>{isArabic ? "نوع القاعدة" : "Rule Type"}</InputLabel>
                                                <Select
                                                    value={rule.type}
                                                    label={isArabic ? "نوع القاعدة" : "Rule Type"}
                                                    onChange={(e) => updateRule(index, 'type', e.target.value)}
                                                >
                                                    <MenuItem value="category">{isArabic ? "الفئة" : "Category"}</MenuItem>
                                                    <MenuItem value="brand">{isArabic ? "العلامة التجارية" : "Brand"}</MenuItem>
                                                    <MenuItem value="tag">{isArabic ? "الوسم" : "Tag"}</MenuItem>
                                                    <MenuItem value="price_range">{isArabic ? "نطاق السعر" : "Price Range"}</MenuItem>
                                                    <MenuItem value="new_arrivals">{isArabic ? "وصل حديثاً" : "New Arrivals"}</MenuItem>
                                                    <MenuItem value="on_sale">{isArabic ? "عليه خصم" : "On Sale"}</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>{isArabic ? "المشغل" : "Operator"}</InputLabel>
                                                <Select
                                                    value={rule.operator || "and"}
                                                    label={isArabic ? "المشغل" : "Operator"}
                                                    onChange={(e) => updateRule(index, 'operator', e.target.value)}
                                                >
                                                    <MenuItem value="and">{isArabic ? "و (AND)" : "AND"}</MenuItem>
                                                    <MenuItem value="or">{isArabic ? "أو (OR)" : "OR"}</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={12}>
                                            {renderRuleValueInput(rule, index)}
                                        </Grid>

                                    </Grid>
                                </Paper>
                            ))}

                            <Button
                                variant="outlined"
                                onClick={addRule}
                                fullWidth
                                sx={{ mt: 2 }}
                            >
                                {isArabic ? "+ إضافة قاعدة" : "+ Add Rule"}
                            </Button>
                        </Grid>
                    )}

                    {/* Manual Collection Products */}
                    {type === "manual" && (
                        <Grid item xs={12}>
                            <Typography variant="overline" color="text.secondary" fontWeight="bold">
                                {isArabic ? "ربط المنتجات" : "Products Sync"}
                            </Typography>
                            <Divider sx={{ mb: 3, mt: 1 }} />

                            {!isEdit && (
                                <Box sx={{ p: 2, mb: 3, bgcolor: 'warning.light', color: 'warning.contrastText', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" fontWeight="500">
                                        {isArabic
                                            ? "⚠️ يرجى حفظ المجموعة أولاً لتتمكن من اختيار ومزامنة المنتجات يدوياً."
                                            : "⚠️ Please save the collection first to be able to select and sync products manually."}
                                    </Typography>
                                </Box>
                            )}

                            <FormInput
                                label={isArabic ? "اختر المنتجات" : "Select Products"}
                                type="multi_select"
                                name="product_ids"
                                selectedValue={selectedProductIds}
                                handleSelectChange={handleProductChange}
                                options={products.map(p => ({
                                    id: p.id,
                                    name: {
                                        ar: p.name?.ar || p.name?.en,
                                        en: p.name?.en || p.name?.ar
                                    }
                                }))}
                                disabled={!isEdit || loadingProducts}
                            />
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loadingSubmit || (type === 'automatic' && rules.length === 0)}
                        sx={{
                            minWidth: 160,
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            boxShadow: theme.shadows[4],
                        }}
                    >
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث البيانات" : "Update Data") : (isArabic ? "إنشاء المجموعه" : "Create Collection"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditCollection;
